import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * A row that scrolls sideways (product carousels, offer strips). Phones and
 * tablets swipe it as before. On desktop (lg and up), where a mouse can't
 * swipe and the scrollbar is hidden, arrow buttons appear at whichever end
 * has more to show. `className` is the row's own classes — it must include
 * the overflow-x scrolling itself.
 */
export const ScrollRow = ({ className = '', testId, children, ...rest }) => {
  const ref = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 4);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    el.addEventListener('scroll', measure, { passive: true });
    const resize = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    resize?.observe(el);
    return () => {
      el.removeEventListener('scroll', measure);
      resize?.disconnect();
    };
  }, [measure]);

  // The items can change without the row resizing (products loading in).
  useEffect(measure);

  const scroll = (direction) => {
    const el = ref.current;
    if (el) el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  const arrow = 'hidden lg:flex absolute top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full '
    + 'bg-white/95 border border-black/10 shadow-md text-[var(--theme-text-main)] hover:bg-white transition-colors';

  return (
    <div className="relative">
      <div ref={ref} className={className} data-testid={testId} {...rest}>
        {children}
      </div>
      {!atStart && (
        <button type="button" onClick={() => scroll(-1)} className={`${arrow} left-1`} aria-label="Scroll left" data-testid={testId ? `${testId}-prev` : undefined}>
          <ChevronLeft className="w-5 h-5" aria-hidden="true" />
        </button>
      )}
      {!atEnd && (
        <button type="button" onClick={() => scroll(1)} className={`${arrow} right-1`} aria-label="Scroll right" data-testid={testId ? `${testId}-next` : undefined}>
          <ChevronRight className="w-5 h-5" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};
